import { useQuery } from '@apollo/client';
import { ReactNode, useState } from 'react';
import { CURRENT_USER } from 'services/auth';
import { CommunityUser, ConnectedAccount, User, GeoIpLocation } from 'services/graphqltypes';
import * as Sentry from "@sentry/react";
import { DefaultGenerics, StreamChat } from 'stream-chat';
import { StreamChatClientComponent } from './StreamChatClientComponent';
import { UserContext, UserPermissions } from './UserContext';

function UserContextProvider({ children }: { children: ReactNode }) {

  const { data, refetch: refetchCurrentUser, loading } = useQuery(CURRENT_USER);

  const currentUser = data?.currentUser as User;
  const currentUserStreamToken = data?.currentUserStreamToken as string;
  const currentUserGeoIPLocation = data?.currentUserGeoIPLocation?.latitude && data?.currentUserGeoIPLocation as GeoIpLocation; // leave undefined if we don't get it (not using the error field)

  Sentry.setUser({ id: currentUser?.id, username: currentUser?.username });

  const isAuthenticated = !!currentUser?.id;

  const currentUserPermissions = currentUser?.communityUsers?.nodes.map((communityUser: CommunityUser): UserPermissions => {
    const communityRolePermissions = communityUser.communityRolePermissions;
    let permissions = [] as any;
    if (communityRolePermissions) {
      permissions = Object.keys(communityRolePermissions).filter((key) => {
        return communityRolePermissions[key] === true;
      });
    }
    return {
      communityId: communityUser.communityId,
      communityName: communityUser.community.name,
      communitySlug: communityUser.community.slug,
      role: communityUser.communityRole.name,
      permissions,
    };
  });

  const currentUserChildren = currentUser?.childConnectedAccounts?.nodes.map((connectedAccount: ConnectedAccount) => {
    return connectedAccount.connectedUser;
  });

  // getstream chat
  const [unreadCount, setUnreadCount] = useState<number>();
  const [streamChatClient, setStreamChatClient] = useState<StreamChat<DefaultGenerics>>();

  const userContextValues = {
    loading,
    isAuthenticated,
    currentUser,
    currentUserGeoIPLocation,
    refetchCurrentUser,
    currentUserPermissions,
    currentUserChildren,
    streamChatClient,
    unreadCount,
  };

  return (
    <UserContext.Provider value={{ ...userContextValues }}>
      { // only render StreamChatClientComponent if we have a currentUser
        currentUser?.id && <StreamChatClientComponent
          currentUser={currentUser}
          currentUserStreamToken={currentUserStreamToken}
          setStreamChatClient={setStreamChatClient}
          setUnreadCount={setUnreadCount}
        />
      }
      {children}
    </UserContext.Provider>
  );
}

export default UserContextProvider;
